// src/pages/AdminPage.js
import React, { useState, useEffect, useCallback } from "react";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { PageHeader, LoadingScreen, Spinner } from "../components/ui";

// MUST be outside component to prevent focus loss
function InputField({ label, name, value, placeholder, type, onChange, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-cosmic-400 uppercase tracking-widest mb-1.5">{label}</label>
      <input type={type||"text"} name={name} value={value||""} onChange={onChange}
        placeholder={placeholder||label} required={required}
        className="w-full px-4 py-2.5 rounded-xl bg-cosmic-950/80 border border-cosmic-700/50 text-cosmic-100
                   placeholder-cosmic-600 focus:outline-none focus:border-cosmic-400 transition-all text-sm" />
    </div>
  );
}

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
  </svg>
);

export default function AdminPage() {
  const { user, login }         = useAuth();
  const [tab, setTab]           = useState("users");
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState({ type:"", text:"" });

  // User editing
  const [editing, setEditing]   = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleting, setDeleting] = useState(null);

  // Create user
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name:"", email:"", password:"", role:"user" });
  const [creating, setCreating]     = useState(false);

  // Settings
  const [settings, setSettings]         = useState({});
  const [settLoading, setSettLoading]   = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Own profile
  const [profileForm, setProfileForm] = useState({ name: user?.name||"", email: user?.email||"", password:"" });
  const [savingProfile, setSavingProfile] = useState(false);

  // Admin kundalis and matches
  const [kundalis, setKundalis]   = useState([]);
  const [matches, setMatches]     = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  const showMsg = useCallback((type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type:"", text:"" }), 4000);
  }, []);

  useEffect(() => { fetchUsers(); fetchSettings(); }, []);

  useEffect(() => {
    if (tab === "kundalis") fetchAllKundalis();
    if (tab === "matches")  fetchAllMatches();
  }, [tab]);

  const fetchUsers = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users.php"); setUsers(data.users||[]); }
    catch(e) { showMsg("error", e.response?.data?.message||"Failed to load users"); }
    setLoading(false);
  };

  const fetchSettings = async () => {
    setSettLoading(true);
    try { const { data } = await api.get("/admin/users.php?settings=1"); if(data.settings) setSettings(prev=>({...prev,...data.settings})); }
    catch(e) {}
    setSettLoading(false);
  };

  const fetchAllKundalis = async () => {
    setDataLoading(true);
    try { const { data } = await api.get("/kundli/kundli.php"); setKundalis(data.kundlis||[]); }
    catch(e) {}
    setDataLoading(false);
  };

  const fetchAllMatches = async () => {
    setDataLoading(true);
    try { const { data } = await api.get("/matchmaking/match.php"); setMatches(data.matches||[]); }
    catch(e) {}
    setDataLoading(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm("Delete user \""+name+"\"? This will also delete their kundalis and matches. Cannot be undone.")) return;
    setDeleting(id);
    try {
      await api.delete("/admin/users.php?id="+id);
      showMsg("success", "User \""+name+"\" deleted.");
      fetchUsers();
    } catch(e) { showMsg("error", e.response?.data?.message||"Delete failed"); }
    setDeleting(null);
  };

  const handleEditOpen = (u) => { setEditing(u.id); setEditForm({ name:u.name, email:u.email, role:u.role, password:"" }); };

  const handleEditSave = async (id) => {
    try {
      await api.put("/admin/users.php?id="+id, editForm);
      showMsg("success", "User updated successfully.");
      setEditing(null);
      fetchUsers();
    } catch(e) { showMsg("error", e.response?.data?.message||"Update failed"); }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      await api.post("/admin/users.php", createForm);
      showMsg("success", "User \""+createForm.name+"\" created successfully.");
      setShowCreate(false);
      setCreateForm({ name:"", email:"", password:"", role:"user" });
      fetchUsers();
    } catch(e) { showMsg("error", e.response?.data?.message||"Create failed"); }
    setCreating(false);
  };

  const handleSettChange = (e) => { const {name,value}=e.target; setSettings(prev=>({...prev,[name]:value})); };

  const handleSettSave = async () => {
    setSavingSettings(true);
    try { await api.post("/admin/users.php?settings=1", settings); showMsg("success","Settings saved!"); }
    catch(e) { showMsg("error", e.response?.data?.message||"Save failed"); }
    setSavingSettings(false);
  };

  const handleProfileChange = (e) => { const {name,value}=e.target; setProfileForm(prev=>({...prev,[name]:value})); };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.put("/admin/users.php?me=1", profileForm);
      // Update localStorage so sidebar updates immediately
      if (data.user) {
        const updatedUser = { ...user, name: data.user.name, email: data.user.email };
        localStorage.setItem("astro_user", JSON.stringify(updatedUser));
        window.dispatchEvent(new Event("storage")); // trigger re-read in AuthContext
      }
      showMsg("success", "Profile updated! Sidebar will update on next login if name changed.");
    } catch(e) { showMsg("error", e.response?.data?.message||"Update failed"); }
    setSavingProfile(false);
  };

  const handleDeleteKundali = async (id, name) => {
    if (!window.confirm("Delete Kundali for \""+name+"\"?")) return;
    try { await api.delete("/kundli/kundli.php?id="+id); setKundalis(k=>k.filter(x=>x.id!==id)); showMsg("success","Kundali deleted."); }
    catch(e) { showMsg("error","Delete failed"); }
  };

  const handleDeleteMatch = async (id, n1, n2) => {
    if (!window.confirm("Delete match for "+n1+" & "+n2+"?")) return;
    try { await api.delete("/matchmaking/match.php?id="+id); setMatches(m=>m.filter(x=>x.id!==id)); showMsg("success","Match deleted."); }
    catch(e) { showMsg("error","Delete failed"); }
  };

  const stats = {
    total:   users.length,
    admins:  users.filter(u=>u.role==="admin").length,
    kundli:  users.reduce((s,u)=>s+(parseInt(u.kundli_count)||0),0),
    matches: users.reduce((s,u)=>s+(parseInt(u.match_count)||0),0),
  };

  const verdictColor = s => s>=80?"text-green-400":s>=65?"text-yellow-400":"text-red-400";

  const TABS = [
    ["users","👥 Users"],["kundalis","📜 All Kundalis"],["matches","💞 All Matches"],
    ["settings","⚙️ Settings"],["profile","👤 My Profile"],
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader icon="⚙️" title="Admin Panel" subtitle="Manage users, kundalis, matches and platform settings" />

      {msg.text && (
        <div className={"mb-5 flex items-start gap-2.5 px-4 py-3 rounded-xl border text-sm "+
          (msg.type==="success"?"bg-green-900/30 border-green-500/40 text-green-300":"bg-red-900/30 border-red-500/40 text-red-300")}>
          <span>{msg.type==="success"?"✅":"⚠️"}</span><span>{msg.text}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[["👥","Total Users",stats.total],["🛡️","Admins",stats.admins],["📜","Kundalis",stats.kundli],["💞","Matches",stats.matches]].map(([icon,label,val])=>(
          <div key={label} className="cosmic-card p-4 text-center">
            <div className="text-2xl mb-1">{icon}</div>
            <p className="font-cinzel text-2xl font-bold text-white">{val}</p>
            <p className="text-xs text-cosmic-400 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)}
            className={"px-4 py-2 rounded-xl text-sm font-semibold transition-all "+
              (tab===id?"bg-cosmic-600 text-white":"text-cosmic-400 hover:text-white hover:bg-cosmic-800/50")}>
            {label}
          </button>
        ))}
      </div>

      {/* ── USERS ── */}
      {tab==="users" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-cinzel font-semibold text-white">All Users</h2>
            <div className="flex gap-2">
              <button onClick={()=>setShowCreate(true)}
                className="btn-cosmic text-sm py-2 px-4 flex items-center gap-2">
                ➕ Create User
              </button>
              <button onClick={fetchUsers}
                className="text-sm text-cosmic-400 hover:text-white border border-cosmic-700/40 rounded-lg px-3 py-2 transition-all">
                ↻ Refresh
              </button>
            </div>
          </div>

          {/* Create User Form */}
          {showCreate && (
            <div className="cosmic-card p-6 border border-green-700/30 bg-green-900/5">
              <h3 className="font-cinzel font-bold text-green-400 mb-4">➕ Create New User</h3>
              <form onSubmit={handleCreate}>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <InputField label="Full Name" name="name" value={createForm.name} placeholder="Enter name" required
                    onChange={e=>setCreateForm(f=>({...f,name:e.target.value}))} />
                  <InputField label="Email" name="email" type="email" value={createForm.email} placeholder="user@example.com" required
                    onChange={e=>setCreateForm(f=>({...f,email:e.target.value}))} />
                  <InputField label="Password" name="password" type="password" value={createForm.password} placeholder="Set password" required
                    onChange={e=>setCreateForm(f=>({...f,password:e.target.value}))} />
                  <div>
                    <label className="block text-xs font-semibold text-cosmic-400 uppercase tracking-widest mb-1.5">Role</label>
                    <select value={createForm.role} onChange={e=>setCreateForm(f=>({...f,role:e.target.value}))}
                      className="w-full px-4 py-2.5 rounded-xl bg-cosmic-950/80 border border-cosmic-700/50 text-cosmic-100 focus:outline-none focus:border-cosmic-400 text-sm">
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={creating} className="btn-cosmic py-2 px-5">
                    {creating?<span className="flex items-center gap-2"><Spinner size="sm"/>Creating...</span>:"✅ Create User"}
                  </button>
                  <button type="button" onClick={()=>{setShowCreate(false);setCreateForm({name:"",email:"",password:"",role:"user"});}} className="btn-outline py-2 px-5">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {/* Users Table */}
          {loading ? <LoadingScreen /> : (
            <div className="cosmic-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-cosmic-900/40 border-b border-cosmic-700/30">
                      {["ID","Name","Email","Role","Kundlis","Matches","Joined","Actions"].map(h=>(
                        <th key={h} className="text-left text-cosmic-400 px-4 py-3 whitespace-nowrap text-xs uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u=>(
                      <React.Fragment key={u.id}>
                        <tr className="border-b border-cosmic-800/30 hover:bg-cosmic-800/20">
                          <td className="px-4 py-3 text-cosmic-500 text-xs">#{u.id}</td>
                          <td className="px-4 py-3 font-semibold text-white">{u.name}</td>
                          <td className="px-4 py-3 text-cosmic-300 text-xs">{u.email}</td>
                          <td className="px-4 py-3">
                            <span className={"px-2 py-0.5 rounded-full text-xs font-semibold border "+
                              (u.role==="admin"?"bg-yellow-500/20 text-yellow-300 border-yellow-500/30":"bg-cosmic-700/40 text-cosmic-400 border-cosmic-600/30")}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-cosmic-300 text-center">{u.kundli_count}</td>
                          <td className="px-4 py-3 text-cosmic-300 text-center">{u.match_count}</td>
                          <td className="px-4 py-3 text-cosmic-500 text-xs whitespace-nowrap">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1.5">
                              <button onClick={()=>editing===u.id?setEditing(null):handleEditOpen(u)}
                                className={"text-xs font-semibold border rounded-lg px-2.5 py-1.5 transition-all "+
                                  (editing===u.id?"bg-blue-600 text-white border-blue-500":"text-blue-400 border-blue-500/30 hover:bg-blue-900/20")}>
                                ✏️ {editing===u.id?"Close":"Edit"}
                              </button>
                              {u.id !== parseInt(localStorage.getItem("astro_user") && JSON.parse(localStorage.getItem("astro_user")||"{}").id) && u.role!=="admin" && (
                                <button onClick={()=>handleDelete(u.id,u.name)} disabled={deleting===u.id}
                                  className="flex items-center gap-1 text-red-400 text-xs font-semibold border border-red-500/30 rounded-lg px-2.5 py-1.5 hover:bg-red-900/20 transition-all disabled:opacity-40">
                                  {deleting===u.id?"...": <><DeleteIcon/></>}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {editing===u.id && (
                          <tr className="border-b border-cosmic-700/30 bg-cosmic-800/10">
                            <td colSpan={8} className="px-6 py-4">
                              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                                <InputField label="Name" name="name" value={editForm.name}
                                  onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} />
                                <InputField label="Email" name="email" type="email" value={editForm.email}
                                  onChange={e=>setEditForm(f=>({...f,email:e.target.value}))} />
                                <div>
                                  <label className="block text-xs font-semibold text-cosmic-400 uppercase tracking-widest mb-1.5">Role</label>
                                  <select className="w-full px-4 py-2.5 rounded-xl bg-cosmic-950/80 border border-cosmic-700/50 text-cosmic-100 focus:outline-none text-sm"
                                    value={editForm.role} onChange={e=>setEditForm(f=>({...f,role:e.target.value}))}>
                                    <option value="user">User</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                </div>
                                <InputField label="New Password (optional)" name="password" type="password" value={editForm.password||""}
                                  placeholder="Leave blank to keep" onChange={e=>setEditForm(f=>({...f,password:e.target.value}))} />
                              </div>
                              <div className="flex gap-2 mt-3">
                                <button onClick={()=>handleEditSave(u.id)} className="btn-cosmic text-xs py-1.5 px-4">Save Changes</button>
                                <button onClick={()=>setEditing(null)} className="btn-outline text-xs py-1.5 px-4">Cancel</button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ALL KUNDALIS ── */}
      {tab==="kundalis" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-cinzel font-semibold text-white">All Kundalis (Platform-wide)</h2>
            <button onClick={fetchAllKundalis} className="text-sm text-cosmic-400 hover:text-white border border-cosmic-700/40 rounded-lg px-3 py-2">↻ Refresh</button>
          </div>
          {dataLoading ? <LoadingScreen /> : kundalis.length===0 ? (
            <div className="cosmic-card p-8 text-center text-cosmic-400">No kundalis generated yet.</div>
          ) : (
            <div className="cosmic-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-cosmic-900/40 border-b border-cosmic-700/30">
                      {["#","Name","DOB","Time","Place","Gender","Owner","Created","Action"].map(h=>(
                        <th key={h} className="text-left text-cosmic-400 px-3 py-3 whitespace-nowrap text-xs uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kundalis.map(k=>(
                      <tr key={k.id} className="border-b border-cosmic-800/30 hover:bg-cosmic-800/20">
                        <td className="px-3 py-2.5 text-cosmic-500 text-xs">#{k.id}</td>
                        <td className="px-3 py-2.5 font-semibold text-white">{k.name}</td>
                        <td className="px-3 py-2.5 text-cosmic-300 text-xs">{k.dob}</td>
                        <td className="px-3 py-2.5 text-cosmic-300 text-xs">{k.time_of_birth}</td>
                        <td className="px-3 py-2.5 text-cosmic-400 text-xs max-w-[120px] truncate">{k.place_of_birth}</td>
                        <td className="px-3 py-2.5 text-cosmic-400 text-xs">{k.gender}</td>
                        <td className="px-3 py-2.5">
                          <span className="text-xs bg-cosmic-700/40 text-cosmic-300 px-2 py-0.5 rounded-full">{k.owner_name||"—"}</span>
                        </td>
                        <td className="px-3 py-2.5 text-cosmic-500 text-xs whitespace-nowrap">{new Date(k.created_at).toLocaleDateString()}</td>
                        <td className="px-3 py-2.5">
                          <button onClick={()=>handleDeleteKundali(k.id,k.name)} title="Delete kundali"
                            className="flex items-center justify-center w-8 h-8 text-red-400 hover:text-white hover:bg-red-600 border border-red-500/30 rounded-lg transition-all">
                            <DeleteIcon/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ALL MATCHES ── */}
      {tab==="matches" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-cinzel font-semibold text-white">All Match Reports (Platform-wide)</h2>
            <button onClick={fetchAllMatches} className="text-sm text-cosmic-400 hover:text-white border border-cosmic-700/40 rounded-lg px-3 py-2">↻ Refresh</button>
          </div>
          {dataLoading ? <LoadingScreen /> : matches.length===0 ? (
            <div className="cosmic-card p-8 text-center text-cosmic-400">No match reports generated yet.</div>
          ) : (
            <div className="cosmic-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-cosmic-900/40 border-b border-cosmic-700/30">
                      {["#","Person 1","Person 2","Signs","Score","Owner","Date","Action"].map(h=>(
                        <th key={h} className="text-left text-cosmic-400 px-3 py-3 whitespace-nowrap text-xs uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map(m=>(
                      <tr key={m.id} className="border-b border-cosmic-800/30 hover:bg-cosmic-800/20">
                        <td className="px-3 py-2.5 text-cosmic-500 text-xs">#{m.id}</td>
                        <td className="px-3 py-2.5 font-semibold text-white">{m.person1_name}</td>
                        <td className="px-3 py-2.5 font-semibold text-white">{m.person2_name}</td>
                        <td className="px-3 py-2.5 text-cosmic-300 text-xs">{m.person1_sign} + {m.person2_sign}</td>
                        <td className="px-3 py-2.5">
                          <span className={"font-cinzel font-bold text-sm "+verdictColor(m.score)}>{m.score}%</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-xs bg-cosmic-700/40 text-cosmic-300 px-2 py-0.5 rounded-full">{m.owner_name||"—"}</span>
                        </td>
                        <td className="px-3 py-2.5 text-cosmic-500 text-xs whitespace-nowrap">{new Date(m.created_at).toLocaleDateString()}</td>
                        <td className="px-3 py-2.5">
                          <button onClick={()=>handleDeleteMatch(m.id,m.person1_name,m.person2_name)} title="Delete match"
                            className="flex items-center justify-center w-8 h-8 text-red-400 hover:text-white hover:bg-red-600 border border-red-500/30 rounded-lg transition-all">
                            <DeleteIcon/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SETTINGS ── */}
      {tab==="settings" && (
        <div className="space-y-6">
          {settLoading ? <LoadingScreen /> : (
            <>
              <div className="cosmic-card p-6">
                <h3 className="font-cinzel font-bold text-white mb-5 flex items-center gap-2"><span>🏢</span> Business Identity</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <InputField label="Business Name"    name="business_name"    value={settings.business_name}    placeholder="AstroVeda"         onChange={handleSettChange} />
                  <InputField label="Business Tagline" name="business_tagline" value={settings.business_tagline} placeholder="Cosmic Insights"    onChange={handleSettChange} />
                  <InputField label="Logo Emoji"       name="logo_text"        value={settings.logo_text}        placeholder="🔮"                 onChange={handleSettChange} />
                  <InputField label="Footer Text"      name="footer_text"      value={settings.footer_text}      placeholder="AstroVeda..."       onChange={handleSettChange} />
                  <div className="sm:col-span-2">
                    <InputField label="Website" name="website" value={settings.website} placeholder="www.yourdomain.com" onChange={handleSettChange} />
                  </div>
                </div>
              </div>
              <div className="cosmic-card p-6">
                <h3 className="font-cinzel font-bold text-white mb-5 flex items-center gap-2"><span>🙏</span> Pandit / Astrologer Details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <InputField label="Pandit Name"  name="pandit_name"  value={settings.pandit_name}  placeholder="Pt. Sharma Ji"   onChange={handleSettChange} />
                  <InputField label="Title"        name="pandit_title" value={settings.pandit_title} placeholder="Jyotish Acharya" onChange={handleSettChange} />
                </div>
                <div className="mt-4 p-3 bg-amber-900/10 border border-amber-700/30 rounded-xl">
                  <p className="text-amber-400 text-xs font-semibold mb-1">Preview on Kundali PDF:</p>
                  <p className="text-white font-cinzel font-bold">{settings.pandit_name||"Pt. Sharma Ji"}</p>
                  <p className="text-cosmic-400 text-sm">{settings.pandit_title||"Jyotish Acharya"} — {settings.business_name||"AstroVeda"}</p>
                </div>
              </div>
              <div className="cosmic-card p-6">
                <h3 className="font-cinzel font-bold text-white mb-5 flex items-center gap-2"><span>📞</span> Contact Details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <InputField label="Phone"   name="contact_phone" value={settings.contact_phone} placeholder="+91 98765 43210"     onChange={handleSettChange} />
                  <InputField label="Email"   name="contact_email" type="email" value={settings.contact_email} placeholder="info@astroveda.com" onChange={handleSettChange} />
                  <div className="sm:col-span-2">
                    <InputField label="Address" name="address" value={settings.address} placeholder="New Delhi, India" onChange={handleSettChange} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button onClick={handleSettSave} disabled={savingSettings} className="btn-cosmic px-8 py-3">
                  {savingSettings?<span className="flex items-center gap-2"><Spinner size="sm"/>Saving...</span>:"💾 Save All Settings"}
                </button>
                <p className="text-cosmic-500 text-xs italic">Changes apply to new Kundali PDFs immediately</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── MY PROFILE ── */}
      {tab==="profile" && (
        <div className="cosmic-card p-6 max-w-lg">
          <h3 className="font-cinzel font-bold text-white mb-5 flex items-center gap-2"><span>👤</span> My Profile</h3>
          <div className="flex items-center gap-4 mb-6 p-4 bg-cosmic-800/30 rounded-xl">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cosmic-500 to-purple-700 flex items-center justify-center text-white font-bold text-xl">
              {(profileForm.name||user?.name||"?")[0]?.toUpperCase()}
            </div>
            <div>
              <p className="font-cinzel font-bold text-white text-lg">{profileForm.name||user?.name}</p>
              <p className="text-cosmic-400 text-sm">{user?.email}</p>
              <span className="text-xs bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 px-2 py-0.5 rounded-full">Admin</span>
            </div>
          </div>
          <div className="space-y-4">
            <InputField label="Full Name"        name="name"     value={profileForm.name}     placeholder="Your name"   onChange={handleProfileChange} />
            <InputField label="Email Address"    name="email"    type="email" value={profileForm.email}    placeholder="your@email.com" onChange={handleProfileChange} />
            <InputField label="New Password"     name="password" type="password" value={profileForm.password} placeholder="Leave blank to keep current" onChange={handleProfileChange} />
          </div>
          <div className="mt-5 p-3 bg-amber-900/10 border border-amber-700/30 rounded-xl">
            <p className="text-amber-400 text-xs">⚠️ After updating your name, please sign out and sign in again for the sidebar name to refresh.</p>
          </div>
          <button onClick={handleProfileSave} disabled={savingProfile} className="btn-cosmic w-full mt-5 py-3">
            {savingProfile?<span className="flex items-center justify-center gap-2"><Spinner size="sm"/>Saving...</span>:"💾 Save Profile"}
          </button>
        </div>
      )}
    </div>
  );
}
